import { post } from "utils/BaseRequest";

const SIWE_API_BASE_URL = "https://api.pinksale.finance";

export const login = async (username: string, password: string) => {
  const { data } = await post("/auth/login", {
    username,
    password,
  });

  return data;
};

/**
 * Fetches a nonce from the SIWE API for authentication
 * @returns {Promise<string>} The nonce string
 */
export const fetchNonce = async (): Promise<string> => {
  const response = await fetch(`${SIWE_API_BASE_URL}/api/v1/auth/nonce`, {
    method: "GET",
    credentials: "include", // Important: include cookies for session
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch nonce: ${response.statusText}`);
  }

  return await response.text();
};

/**
 * Verifies the SIWE message and signature
 * @param {Object} params - Verification parameters
 * @param {string} params.message - SIWE message string
 * @param {string} params.signature - Signature of the message
 * @param {string} [params.type] - Type: 'EVM' | 'SOLANA' | 'SUI' (defaults to 'EVM')
 * @param {string} [params.domain] - Domain for verification
 * @param {number} [params.chainId] - Chain ID
 * @returns {Promise<any>} User object with token
 */
export const verifySignature = async ({
  message,
  signature,
  type = "EVM",
  domain,
  chainId,
}: {
  message: string;
  signature: string;
  type?: string;
  domain?: string;
  chainId?: number;
}): Promise<any> => {
  const response = await fetch(`${SIWE_API_BASE_URL}/api/v1/auth/verify`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    credentials: "include", // Important: include cookies for session
    body: JSON.stringify({
      message,
      signature,
      type,
      domain,
      chainId,
    }),
  });

  if (!response.ok) {
    const errorData = await response
      .json()
      .catch(() => ({ message: response.statusText }));
    throw new Error(
      errorData.message || `Verification failed: ${response.statusText}`
    );
  }

  return await response.json();
};
